<template>
  <div>
    <div v-if="loading" class="text-center pa-2">
      <circles-loader />
    </div>
    <div v-else-if="instance">
      <div class="mb-2">
        <div class="text-subtitle-1 font-weight-bold">
          {{ instance.syllabusTemplate ? instance.syllabusTemplate.name : '' }}
        </div>
        <div v-if="instance.currentLevel" class="supporting-text">
          {{ $t("message.current_level") }}:
          {{ useBelts ? $t("message." + instance.currentLevel) : instance.currentLevel }}
        </div>
      </div>
      <v-list v-if="levels.length > 0" class="pa-0">
        <template v-for="(d, index) in levels">
          <v-card outlined elevation="0" class="pa-0 ma-0 mb-2" :key="'syllabus_instance_level_' + index + '_' + d._id">
            <v-subheader>
              <v-progress-linear v-if="useBelts" :color="getBeltColorForProgress(d.level)" height="20"
                :value="d.overallPercent" striped
                :style="(d.level && d.level.indexOf('white') >= 0 ? 'border: 1px solid gray;' : '') + (instance.currentLevel == d.level ? '' : 'opacity: 0.5;')">
                <template v-slot:default="{ value }">
                  <strong :class="getProgressTextColor(instance.currentLevel, true) + '--text'">{{ Math.ceil(value)
                    }}%</strong>
                </template>
              </v-progress-linear>
              <div v-else style="display:flex;align-items:center;gap:8px;width:100%">
                <v-avatar v-if="images[d.level]" size="20">
                  <ImageDisplay :src="images[d.level]" />
                </v-avatar>
                <v-progress-linear style="flex:1" :color="colors[d.level] ? colors[d.level] : getColor('light')"
                  height="20" :value="d.overallPercent" striped
                  :style="(colors[d.level] ? 'color:white;' : '') + (instance.currentLevel == d.level ? '' : 'opacity: 0.5;')">
                  <template v-slot:default="{ value }">
                    <strong> {{ d.level }}: {{ Math.ceil(value) }}%</strong>
                  </template>
                </v-progress-linear>
              </div>
            </v-subheader>
            <v-list-item v-if="d.createdOn">
              <v-list-item-content>
                <v-list-item-title>
                  <v-icon size="20" class="mr-1">mdi-ray-start-arrow</v-icon>
                  {{ $t('message.started') }}: {{ $d((new Date(d.createdOn)), "month_day_year", $i18n.locale) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t('message.level') }}:
                  <span v-if="useBelts">
                    <belt-display size="15" :belt="d.level" :show-text="true" />
                  </span>
                  <span v-else-if="images[d.level]">
                    <v-avatar size="15" style="vertical-align:middle">
                      <ImageDisplay :src="images[d.level]" />
                    </v-avatar>
                    {{ d.level }}
                  </span>
                  <span v-else>{{ d.level }}</span>
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  {{ $t('message.days_ago', [daysBetween(new Date(), new Date(d.createdOn))]) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="d.completed">
              <v-list-item-content>
                <v-list-item-title>
                  <v-icon size="20" color="success" class="mr-1">mdi-flag-checkered</v-icon>
                  {{ $t('message.completed') }}: {{ $d((new Date(d.completed)), "month_day_year", $i18n.locale) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t('message.days_ago', [daysBetween(new Date(), new Date(d.completed))]) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="d.completedBy">
                  {{ $t('message.completed_by') }}:
                  <v-avatar size="20" class="mr-1 ml-1">
                    <ImageDisplay :src="d.completedBy.userImageSmall" />
                  </v-avatar>{{ d.completedBy.displayName }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div v-for="(rule, ruleIndex) in d.instanceRules" :key="'syllabus_instance_rule_' + ruleIndex + '_' + d._id">
              <v-list-item @click="doNothing()" class="text-left">
                <v-list-item-content>
                  <div v-if="rule.category">
                    <v-chip outlined x-small color="accent">{{ rule.category }}</v-chip>
                  </div>
                  <v-list-item-title>
                    <span v-if="rule.ruleType == 'checkins'" class="mr-1">
                      <v-icon size="20">mdi-map-marker-outline</v-icon>
                      {{ $t('message.regular_checkins') }}
                    </span>
                    <span v-else-if="rule.ruleType == 'checkins_contains'" class="mr-1">
                      <v-icon size="20">mdi-map-marker-outline</v-icon>{{ rule.ruleId }}
                    </span>
                    <span v-else-if="rule.ruleType == 'checkins_matches'" class="mr-1">
                      <v-icon size="20">mdi-map-marker-outline</v-icon>{{ rule.ruleId }}
                    </span>
                    <span v-else-if="rule.ruleType == 'number'" class="mr-1">
                      <v-icon size="20">mdi-chevron-double-up</v-icon>{{ rule.ruleId }}
                    </span>
                    <span v-else-if="rule.ruleType == 'boolean'" class="mr-1">
                      <v-icon size="20">mdi-check-circle-outline</v-icon>{{ rule.ruleId }}
                    </span>
                    <span v-else-if="rule.ruleType == 'video'" class="mr-1">
                      <v-icon size="20">mdi-video-outline</v-icon>{{ rule.ruleId }}
                    </span>
                    <span v-else-if="rule.ruleType == 'days'" class="mr-1">
                      <v-icon size="20">mdi-calendar-clock</v-icon>{{ rule.ruleId }}
                    </span>
                    <span v-else-if="rule.ruleType == 'text'" class="mr-1">
                      <v-icon size="20">mdi-text-box-outline</v-icon>{{ rule.ruleId }}
                    </span>
                    <span v-else-if="rule.ruleType == 'flowchart'" class="mr-1">
                      <v-icon size="20">mdi-sitemap-outline</v-icon>{{ rule.ruleId }}
                    </span>
                  </v-list-item-title>
                  <div v-if="rule.details || rule.text || rule.longText || rule.updatedOn">
                    <v-btn text x-small class="pl-0" @click.stop="$set(rule, 'expanded', !rule.expanded)">
                      <v-icon x-small class="mr-1">{{ rule.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      {{ $t('message.details') }}
                    </v-btn>
                    <div v-if="rule.expanded" class="text-caption grey--text mt-1 pb-1">
                      <div v-if="rule.details" class="mb-1">{{ rule.details }}</div>
                      <div v-if="rule.text" class="mb-1">{{ rule.text }}</div>
                      <div v-if="rule.longText" class="mb-1">{{ rule.longText }}</div>
                      <div v-if="rule.updatedOn">
                        <v-icon size="12" class="mr-1">mdi-clock-outline</v-icon>
                        <span v-if="rule.ruleType === 'boolean' && rule.booleanValue">{{ $t('message.completed')
                          }}:</span>
                        <span v-else>{{ $t('message.updated') }}:</span>
                        {{ $d(new Date(rule.updatedOn), "month_day_year", $i18n.locale) }}
                      </div>
                    </div>
                  </div>
                </v-list-item-content>
                <v-list-item-action v-if="instance.currentLevel == d.level">
                  <v-text-field hide-details outlined dense v-if="isCheckinsRule(rule.ruleType)"
                    :disabled="!rule.enableEditing" @change="updateInstanceDataRules($event, d, rule.ruleId)"
                    v-model="rule.numberValue" :label="'Total: ' + rule.numberNeeded">
                  </v-text-field>
                  <div v-else-if="rule.ruleType == 'days'">
                    {{ rule.numberValue }}/{{ rule.numberNeeded }}
                  </div>
                  <v-text-field outlined dense v-else-if="rule.numberValue != null" type="number"
                    @change="updateInstanceDataRules($event, d, rule.ruleId)" v-model="rule.numberValue"
                    :label="'Total: ' + rule.numberNeeded">
                  </v-text-field>
                  <v-switch v-else @change="updateInstanceDataRules($event, d, rule.ruleId)"
                    v-model="rule.booleanValue"></v-switch>
                </v-list-item-action>
                <v-list-item-action v-else>
                  <v-text-field outlined v-if="rule.ruleId == 'checkins'" :disabled="true" v-model="rule.numberValue"
                    :label="'Total: ' + rule.numberNeeded"></v-text-field>
                  <v-text-field outlined dense v-else-if="rule.numberValue != null" :disabled="true"
                    v-model="rule.numberValue" :label="'Total: ' + rule.numberNeeded">
                  </v-text-field>
                  <v-text-field outlined dense v-else-if="rule.ruleType == 'days'" :disabled="true"
                    v-model="rule.numberValue" :label="$t('message.needed') + ': ' + rule.numberNeeded">
                  </v-text-field>
                  <v-switch v-else :disabled="true" v-model="rule.booleanValue"></v-switch>
                </v-list-item-action>
              </v-list-item>
              <div class="pl-2" v-if="isCheckinsRule(rule.ruleType) && instance.currentLevel == d.level">
                <v-switch small v-model="rule.enableEditing"
                  :label="isSmallerDisplay() ? $t('message.override_checkins') : $t('message.checkin_rules_edit_explained')">
                </v-switch>
              </div>
              <v-divider v-if="ruleIndex + 1 < d.instanceRules.length" :key="ruleIndex"></v-divider>
            </div>
          </v-card>
        </template>
      </v-list>
      <div v-else class="supporting-text">
        {{ $t("message.no_current_level") }}
      </div>
    </div>
  </div>
</template>

<script>
import BeltDisplay from "@/components/displays/BeltDisplay.vue";
import Api from "@/services/Api2";

// One syllabus instance with its levels and editable rules, same as the member page
// (ViewMember.vue). currentLevelOnly hides the older levels.
export default {
  props: {
    instanceId: {
      type: String,
      required: true,
    },
    currentLevelOnly: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BeltDisplay,
  },
  data() {
    return {
      loading: false,
      instance: null,
      colors: {},
      images: {},
    };
  },
  computed: {
    useBelts() {
      return !!(this.instance && this.instance.syllabusTemplate && this.instance.syllabusTemplate.useBelts);
    },
    levels() {
      if (!this.instance || !this.instance.data) return [];
      if (this.currentLevelOnly)
        return this.instance.data.filter((d) => d.level == this.instance.currentLevel);
      return this.instance.data;
    },
  },
  watch: {
    instanceId() {
      this.getSyllabusInstance();
    },
  },
  mounted() {
    this.getSyllabusInstance();
  },
  methods: {
    isCheckinsRule(ruleType) {
      if (ruleType && ruleType.indexOf('checkins') >= 0)
        return true;
      return false;
    },
    getSyllabusInstance() {
      this.loading = true;
      Api()
        .get("syllabus/academy/" + this.$store.state.managedAcademy + "/instances/" + this.instanceId)
        .then(response => {
          if (response.data && response.data.instance) {
            let instance = response.data.instance;
            if (instance.data) instance.data = instance.data.reverse();
            else instance.data = [];
            const templateRulesDefinitions = response.data.templateLevelRules;
            for (const levelData of instance.data) {
              //Add rule type for each level rule
              if (templateRulesDefinitions && templateRulesDefinitions[levelData.level]) {
                for (let instanceRule of levelData.instanceRules) {
                  if (templateRulesDefinitions[levelData.level][instanceRule.ruleId]) {
                    instanceRule.ruleType = templateRulesDefinitions[levelData.level][instanceRule.ruleId].ruleType;
                    instanceRule.category = templateRulesDefinitions[levelData.level][instanceRule.ruleId].category;
                    instanceRule.details = templateRulesDefinitions[levelData.level][instanceRule.ruleId].details;
                    instanceRule.text = templateRulesDefinitions[levelData.level][instanceRule.ruleId].text;
                    instanceRule.longText = templateRulesDefinitions[levelData.level][instanceRule.ruleId].longText;
                  }
                }
                //Sort the rules by category
                levelData.instanceRules = this.sortInstanceLevelRules(levelData);
              }
            }
            this.colors = response.data.colors ? response.data.colors : {};
            this.images = response.data.images ? response.data.images : {};
            this.instance = instance;
          }
          this.loading = false;
        })
        .catch(e => {
          this.loading = false;
          if (e)
            this.showAlert("error", "Oops!", this.$t('message.error_generic'));
        });
    },
    sortInstanceLevelRules(levelData) {
      const withCategory = [];
      const withoutCategory = [];
      levelData.instanceRules.forEach(item => {
        if (Object.prototype.hasOwnProperty.call(item, 'category')) {
          withCategory.push(item);
        } else {
          withoutCategory.push(item);
        }
      });
      // Sort items with 'category' alphabetically by 'category'
      withCategory.sort((a, b) => {
        const catA = a.category || '';
        const catB = b.category || '';
        return catA.localeCompare(catB);
      });
      return [...withCategory, ...withoutCategory];
    },
    updateInstanceDataRules(event, levelData, ruleId) {
      let updatedRule = null;
      for (const r of levelData.instanceRules) {
        if (r.ruleId == ruleId) {
          updatedRule = {
            _id: r._id,
            ruleId: r.ruleId,
            numberValue: r.numberValue != null ? event : null,
            booleanValue: r.booleanValue != null ? event : null,
          };
          break;
        }
      }
      if (updatedRule) {
        Api()
          .patch("syllabus/academy/" + this.$store.state.managedAcademy + "/instances/data/" + levelData._id + "/rules/", updatedRule)
          .then(response => {
            if (response.data) {
              levelData.overallPercent = response.data.progress;
              this.$emit("progress", { level: levelData.level, progress: levelData.overallPercent });
            }
          })
          .catch(e => {
            if (e)
              this.showAlert("error", "Oops!", this.$t('message.error_generic'));
          });
      }
    },
  },
};
</script>
